import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './components/auth/AuthContext';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'oauth-callback' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'oauth-callback' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'oauth-callback',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `OAuth callback action: ${action}`,
    category: 'auth-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `OAuth callback performance: ${operation} took ${duration}ms`,
    category: 'auth-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const logSecurityEvent = (event, context = {}) => {
  Sentry.captureMessage(`OAuth callback security event: ${event}`, {
    level: 'warning',
    tags: { 
      section: 'oauth-security',
      event_type: 'security_event'
    },
    extra: context
  });
};

// Timeout duration for OAuth callback (30 seconds)
const OAUTH_CALLBACK_TIMEOUT_MS = 30000;

const Callback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSocialAuth } = useAuth();
  const hasHandled = useRef(false);

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const componentStartTime = performance.now();
    
    logInfo('OAuth callback component mounted', {
      pathname: location.pathname,
      searchParams: !!location.search,
      timestamp: new Date().toISOString()
    });
    
    logUserAction('callback_component_mounted', {
      hasSearchParams: !!location.search
    });

    Sentry.setContext("oauth-callback", {
      pathname: location.pathname,
      hasSearchParams: !!location.search,
      mountedAt: new Date().toISOString()
    });

    const handleCallback = async () => {
      const callbackStartTime = performance.now();
      
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const provider = params.get('provider') || 'google';
      
      logInfo('Processing OAuth callback parameters', {
        hasCode: !!code,
        hasState: !!state,
        provider: provider,
        searchParamsCount: params.size
      });
      
      logUserAction('callback_processing_started', {
        provider: provider,
        hasCode: !!code,
        hasState: !!state
      });

      if (!code || !state) {
        const securityContext = {
          hasCode: !!code,
          hasState: !!state,
          provider: provider,
          searchParams: location.search,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        };
        
        logSecurityEvent('oauth_callback_missing_parameters', securityContext);
        
        logError('Missing required OAuth parameters', {
          hasCode: !!code,
          hasState: !!state,
          provider: provider,
          paramCount: params.size
        });
        
        logUserAction('callback_failed_missing_params', {
          hasCode: !!code,
          hasState: !!state,
          provider: provider
        });
        
        navigate('/login', {
          state: { error: 'Invalid authentication response. Please try again.' },
          replace: true
        });
        return;
      }

      try {
        const apiCallStartTime = performance.now();
        
        logInfo('Starting OAuth callback via AuthContext', {
          provider: provider
        });
        
        logUserAction('callback_auth_context_started', {
          provider: provider
        });

        // Use AuthContext's handleSocialAuth which routes to handleOAuthCallback
        // when code and state are provided. This ensures proper error mapping,
        // state management, and auth dispatch.
        // Wrap with timeout to prevent indefinite waiting.
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject({
              code: 'REQUEST_TIMEOUT',
              message: 'Authentication is taking longer than expected. Please try again.'
            });
          }, OAUTH_CALLBACK_TIMEOUT_MS);
        });

        const result = await Promise.race([
          handleSocialAuth(provider, 'login', code, state),
          timeoutPromise
        ]);

        const apiCallEndTime = performance.now();
        const apiCallDuration = apiCallEndTime - apiCallStartTime;
        const componentTotalDuration = apiCallEndTime - componentStartTime;

        logPerformance('oauth_callback_auth_context_call', apiCallDuration, {
          provider: provider,
          success: true
        });

        logPerformance('oauth_callback_component_complete', componentTotalDuration, {
          provider: provider,
          success: true
        });

        logInfo('OAuth callback completed successfully', {
          provider: result?.provider || provider,
          totalDuration: `${apiCallDuration.toFixed(2)}ms`,
          componentDuration: `${componentTotalDuration.toFixed(2)}ms`
        });

        logUserAction('callback_success', {
          provider: result?.provider || provider,
          totalDuration: `${apiCallDuration.toFixed(2)}ms`
        });

        Sentry.setContext("oauth-callback-success", {
          provider: result?.provider || provider,
          completedAt: new Date().toISOString(),
          totalDuration: apiCallDuration
        });

        // AuthContext's handleOAuthCallback handles navigation to /dashboard
        // If we reach here and no navigation happened, navigate as fallback
        if (result?.status === 'SUCCESS') {
          logInfo('OAuth callback success - AuthContext handled navigation');
        }

      } catch (error) {
        const errorEndTime = performance.now();
        const errorDuration = errorEndTime - callbackStartTime;
        const componentErrorDuration = errorEndTime - componentStartTime;
        
        logPerformance('oauth_callback_error', errorDuration, {
          provider: provider,
          errorName: error.name || 'unknown',
          errorCode: error.code || 'unknown'
        });
        
        logPerformance('oauth_callback_component_error', componentErrorDuration, {
          provider: provider,
          errorName: error.name || 'unknown'
        });
        
        logError('OAuth callback processing failed', {
          provider: provider,
          errorName: error.name || 'unknown',
          errorCode: error.code || 'unknown',
          errorMessage: error.message || 'unknown',
          processingDuration: `${errorDuration.toFixed(2)}ms`,
          componentDuration: `${componentErrorDuration.toFixed(2)}ms`,
          isNetworkError: error.code === 'NETWORK_ERROR',
          isTimeout: error.code === 'REQUEST_TIMEOUT',
          timestamp: new Date().toISOString()
        }, error instanceof Error ? error : null);
        
        logUserAction('callback_processing_failed', {
          provider: provider,
          errorCode: error.code || 'unknown',
          errorMessage: error.message || 'unknown',
          duration: `${errorDuration.toFixed(2)}ms`
        });

        Sentry.setContext("oauth-callback-failure", {
          provider: provider,
          errorCode: error.code || 'unknown',
          errorMessage: error.message || 'unknown',
          failedAt: new Date().toISOString(),
          processingDuration: errorDuration
        });

        // error.message is already mapped through AuthContext's GENERIC_ERROR_MESSAGES
        // If error came from timeout, it has our custom message
        // Fallback to a generic user-friendly message if somehow unmapped
        const userMessage = error.message || 'Authentication failed. Please try again.';
        
        logInfo('Navigating to login page after OAuth failure', {
          provider: provider,
          errorMessage: userMessage
        });
        
        logUserAction('navigating_to_login_after_error', {
          provider: provider,
          errorCode: error.code || 'unknown'
        });
        
        navigate('/login', {
          state: { error: userMessage },
          replace: true
        });
      }
    };

    handleCallback();
  }, [location, navigate, handleSocialAuth]);

  // Component cleanup logging
  useEffect(() => {
    return () => {
      logInfo('OAuth callback component unmounting', {
        wasHandled: hasHandled.current
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-t-transparent border-white rounded-full animate-spin mx-auto" />
        <p className="mt-4">Processing authentication...</p>
        <p className="mt-2 text-sm text-gray-400">Please wait while we complete your request</p>
      </div>
    </div>
  );
};

export default Callback;